import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { useAcquirerCopyMutation } from "src/app/features/Acquirer/services/server/AcquirerApi";
import type { AcquirerCopyEntity } from "src/app/features/Acquirer/types/AcquirerCopyEntity";
import { FormManager } from "../../../../../components/Form/FormManager";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName, ResourceVariant } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import acquirerCopyJson from "./acquirer-copy.json";

export const AcquirerCopyForm: FC<FormProps> = ({ runSubmit }) => {
    const formDefinition = acquirerCopyJson as FormJson;

    const [formData, setFormData] = useState<AcquirerCopyEntity>({} as AcquirerCopyEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useAcquirerCopyMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                const { data } = result;

                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formCopySuccess", {
                            id: data.id,
                            resource: "Acquirer",
                        }),
                        autoCloseDisabled: false,
                    }),
                );

                const route = data.physical
                    ? `${BASE_PATH}/${ResourceName.ACQUIRER}/${ResourceVariant.PHYSICAL}/${FORM_ACTION.EDIT}/${data.id}?discriminator=PHYSICAL&id=${data.id}`
                    : `${BASE_PATH}/${ResourceName.ACQUIRER}/${ResourceVariant.VIRTUAL}/${FORM_ACTION.EDIT}/${data.id}?discriminator=VIRTUAL&id=${data.id}`;
                navigate(route);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    const { id } = useParams();
    return (
        <Spin spinning={saveFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<AcquirerCopyEntity>
                    name="AcquirerPhysicalCopyForm"
                    formData={{ ...formData, id }}
                    formLayout={FormLayoutType.DEFAULT}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
